import { useCallback, useEffect, useMemo, useState } from "react";
import { Dial } from "@keydials/ui";
import type { DialLayout, DialModel } from "@keydials/dial-renderer";
import {
  ApiError,
  type FeedMappingView,
  type FeedWiring,
  type GeneratedStatus,
  type InstanceDetail,
} from "@keydials/api-client";
import { segmentNoun } from "@keydials/dial-workspace";
import { Icon } from "../Icon";
import { Modal } from "./Modal";
import { api, getAccessToken } from "../../auth/AuthContext";
import {
  describeSource,
  describeTarget,
  draftFromMapping,
  draftProblems,
  newDraft,
  ownSections,
  sourceStatuses,
  targetStatuses,
  toSaveRequest,
  wireKind,
  wiredSources,
  wiredTargets,
  type WireDraft,
  type WireSource,
} from "../../feedWiring";

/**
 * The recipient's wiring board (KEY-359): their slice on the left, your dial on the right, both live.
 * **Click one wedge, then click the other**, and a wire appears below carrying the one decision left —
 * how their statuses translate into yours.
 *
 * **Why click-then-click and not drag.** A drag needs sustained accuracy at both ends and this codebase
 * has measured its own wedges at 20–40pt; the dial's arcs are already focusable buttons handling Enter
 * and Space, so this is keyboard-operable with no extra work, where a drag would need a parallel keyboard
 * mode built from scratch; and a pending first click with a visible instruction line is a state you can
 * *see*, while a drag in flight is one you can only feel.
 *
 * **The band and the wedge mean different rules.** A wedge on their dial mirrors one action point through
 * a status map. A section band aggregates that whole section into one status of yours — "when everything
 * they've shared is finished, mark my sign-off complete". Nothing is invented for this: the dial already
 * reports the two clicks separately.
 *
 * **Nothing here propagates.** A wire authored on this board is inert until the engine ships — which
 * lands together with its audit surfacing, never before it.
 */

// Fixed hexes rather than theme tokens, matching the dial face and the sharer's paint canvas, which keep
// their own colours across light and dark.
const WIRED = "#4f46e5";
const PICKED = "#f59e0b";
const PLAIN = "#cbd5e1";
const MUTED = "#e8edf3";

const cardStyle = {
  border: "var(--border-w) solid var(--border)",
  borderRadius: "var(--radius-sm)",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
} as const;

const hintStyle = { fontSize: 11.5, color: "var(--text-faint)", margin: 0 } as const;

const selectStyle = {
  padding: "5px 8px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface-2)",
  color: "var(--text)",
  font: "inherit",
  fontSize: 12.5,
} as const;

/** What each health value means in the vocabulary of the board rather than the database. */
const HEALTH: Record<string, { text: string; tone: string }> = {
  Ok: { text: "Ready", tone: "var(--st-ontrack, #16a34a)" },
  FeedNotLive: { text: "Feed stopped", tone: "var(--text-faint)" },
  SourceOutOfScope: { text: "No longer shared with you", tone: "var(--st-overdue)" },
  SourceMissing: { text: "Gone from their dial", tone: "var(--st-overdue)" },
  TargetMissing: { text: "Gone from your dial", tone: "var(--st-overdue)" },
  StatusUnmapped: { text: "Statuses no longer match", tone: "var(--st-overdue)" },
  AttributeIncompatible: { text: "Fields no longer match", tone: "var(--st-overdue)" },
  Invalid: { text: "Needs attention", tone: "var(--st-overdue)" },
};

function Swatch({ colour }: { colour: string }) {
  return (
    <span
      aria-hidden
      style={{ width: 11, height: 11, borderRadius: 3, background: colour, flexShrink: 0, display: "inline-block" }}
    />
  );
}

export function FeedWiringModal({
  feedId,
  instanceId,
  detail,
  onClose,
}: {
  feedId: string;
  instanceId: string;
  detail: InstanceDetail;
  onClose: () => void;
}) {
  const [wiring, setWiring] = useState<FeedWiring | null>(null);
  const [mappings, setMappings] = useState<FeedMappingView[] | null>(null);
  const [pending, setPending] = useState<WireSource | null>(null);
  const [draft, setDraft] = useState<WireDraft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);

  const sections = useMemo(() => ownSections(detail.sections), [detail.sections]);
  // KEY-721: YOUR dial's word for an action point (KEY-539). Their dial's word isn't known here, so the copy about
  // their side keeps the platform's.
  const noun = segmentNoun(detail);

  const reload = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const [w, m] = await Promise.all([api.getFeedWiring(token, feedId), api.listFeedMappings(token, instanceId)]);
      setWiring(w);
      setMappings(m.filter((x) => x.feedId === feedId));
    } catch {
      setWiring(null);
      setMappings([]);
    }
  }, [feedId, instanceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const wires = mappings ?? [];
  const sourcesWired = useMemo(() => wiredSources(wires), [wires]);
  const targetsWired = useMemo(() => wiredTargets(wires), [wires]);

  // On an itemised dial a status lives on an item, so an action point with none is nothing a wire could
  // ever write to. Those are the only invalid targets there are — everything else you can see, you can
  // write to.
  const canTarget = useCallback(
    (sectionKey: string, segmentKey: string) => {
      if (detail.kind !== "Itemised") return true;
      const segment = sections.find((s) => s.key === sectionKey)?.segments.find((g) => g.key === segmentKey);
      return (segment?.items.length ?? 0) > 0;
    },
    [detail.kind, sections],
  );

  const theirModel = useMemo<DialModel>(
    () => ({
      innerRadius: 120,
      midRadius: 300,
      outerRadius: 372,
      sectionGap: 0.012,
      showCentralProgress: false,
      sections: (wiring?.sections ?? []).map((section) => ({
        key: section.key,
        label: section.label,
        segments: section.segments.map((segment) => ({
          key: segment.key,
          label: segment.label,
          colour:
            pending?.kind === "segment" && pending.sectionKey === section.key && pending.segmentKey === segment.key
              ? PICKED
              : pending?.kind === "section" && pending.sectionKey === section.key
                ? PICKED
                : sourcesWired.has(`${section.key}/${segment.key}`) || sourcesWired.has(section.key)
                  ? WIRED
                  : PLAIN,
        })),
      })),
    }),
    [wiring, pending, sourcesWired],
  );

  const yourModel = useMemo<DialModel>(
    () => ({
      innerRadius: 120,
      midRadius: 300,
      outerRadius: 372,
      sectionGap: 0.012,
      showCentralProgress: false,
      sections: sections.map((section) => ({
        key: section.key,
        label: section.label,
        segments: section.segments.map((segment) => ({
          key: segment.key,
          label: segment.name,
          colour: !canTarget(section.key, segment.key)
            ? MUTED
            : targetsWired.has(`${section.key}/${segment.key}`)
              ? WIRED
              : PLAIN,
        })),
      })),
    }),
    [sections, targetsWired, canTarget],
  );

  /** Hatching over what a pending pick cannot land on, drawn only once a first click has been made. */
  const yourOverlay = (layout: DialLayout) => {
    if (!pending) return null;
    return (
      <g pointerEvents="none">
        <defs>
          <pattern id="kd-wire-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#94a3b8" strokeWidth="2" />
          </pattern>
        </defs>
        {layout.segmentArcs
          .filter((arc) => !canTarget(arc.sectionKey, arc.segmentKey))
          .map((arc) => (
            <path key={`${arc.sectionKey}/${arc.segmentKey}`} d={arc.path} fill="url(#kd-wire-hatch)" opacity={0.5} />
          ))}
      </g>
    );
  };

  function pick(source: WireSource) {
    setPending(source);
    setDraft(null);
    setEditingId(null);
    setProblems([]);
  }

  function land(sectionKey: string, segmentKey: string) {
    if (!pending || !wiring || !canTarget(sectionKey, segmentKey)) return;
    setDraft(newDraft(wiring, pending, { sectionKey, segmentKey }, sections));
    setPending(null);
    setProblems([]);
  }

  async function save() {
    if (!draft || !wiring) return;
    const local = draftProblems(draft, wiring);
    if (local.length > 0) {
      setProblems(local);
      return;
    }

    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setProblems([]);
    try {
      const request = toSaveRequest(feedId, draft);
      if (editingId) {
        await api.updateFeedMapping(token, editingId, request);
      } else {
        await api.createFeedMapping(token, instanceId, request);
      }
      setDraft(null);
      setEditingId(null);
      await reload();
    } catch (e) {
      // The server re-derives this from both dials, so its refusal is the one that counts — and it names
      // which end is wrong rather than saying the wire is invalid.
      setProblems(e instanceof ApiError ? (e.problems ?? [e.message]) : ["Something went wrong."]);
    } finally {
      setBusy(false);
    }
  }

  async function run(action: () => Promise<unknown>) {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (e) {
      setProblems(e instanceof ApiError ? (e.problems ?? [e.message]) : ["Something went wrong."]);
    } finally {
      setBusy(false);
    }
  }

  function edit(mapping: FeedMappingView) {
    setDraft(draftFromMapping(mapping));
    setEditingId(mapping.id);
    setPending(null);
    setProblems([]);
  }

  const instruction = pending
    ? `${describeSource(wiring!, pending)} selected. Now click the ${noun.one} on your dial it should set.`
    : draft
      ? "Finish this wire below, or cancel it."
      : "Click something on their dial — a wedge for one action point, the outer band for a whole section.";

  if (!wiring) {
    return (
      <Modal title="Wire this feed in" onClose={onClose} width={720}>
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: 0 }}>
          {mappings === null ? "Loading…" : "This feed isn’t running any more, so there’s nothing to wire."}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Wire this feed in"
      subtitle={`${wiring.sourceOrganisationName} · “${wiring.sourceDialName}” → “${detail.name}”`}
      onClose={onClose}
      width={720}
    >
      <p style={{ ...hintStyle, marginBottom: 10 }}>
        Nothing moves yet — wires you draw here are recorded, and start carrying statuses once feeding-in is
        switched on. You’ll always review an incoming change before it lands unless you say otherwise.
      </p>

      <div role="status" aria-live="polite" style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", marginBottom: 8 }}>
        {instruction}
      </div>

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
        <div style={{ flex: "1 1 260px", maxWidth: 300, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textAlign: "center" }}>
            Their dial · what they feed you
          </div>
          <Dial
            model={theirModel}
            fluid
            ariaLabel="What they feed you — click an action point to read it, or a section band to aggregate it"
            onSelectSection={(sectionKey) => pick({ kind: "section", sectionKey })}
            onSelectSegment={(sectionKey, segmentKey) => pick({ kind: "segment", sectionKey, segmentKey })}
          />
        </div>

        <div style={{ flex: "1 1 260px", maxWidth: 300, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textAlign: "center" }}>
            Your dial · what it sets
          </div>
          <Dial
            model={yourModel}
            fluid
            ariaLabel={`Your dial — click the ${noun.one} this feed should set`}
            onSelectSegment={land}
            overlay={yourOverlay}
          />
        </div>
      </div>

      <p style={{ ...hintStyle, textAlign: "center", marginTop: 4 }}>
        Colour shows what is already wired, not how the work is going.
      </p>

      {draft && (
        <WireEditor
          draft={draft}
          setDraft={setDraft}
          wiring={wiring}
          detail={detail}
          sections={sections}
          problems={problems}
          busy={busy}
          editing={editingId !== null}
          onSave={() => void save()}
          onCancel={() => {
            setDraft(null);
            setEditingId(null);
            setProblems([]);
          }}
        />
      )}

      <h3 style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase", color: "var(--text-muted)", margin: "18px 0 8px" }}>
        Wires from this feed
      </h3>

      {wires.length === 0 && <p style={hintStyle}>Nothing wired yet.</p>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {wires.map((wire) => {
          const health = HEALTH[wire.health] ?? HEALTH.Invalid;
          return (
            <div key={wire.id} style={cardStyle}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>{wire.name}</span>
                <span style={{ fontSize: 11.5, color: health.tone, fontWeight: 600 }}>{health.text}</span>
                {wire.isPaused && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>Paused</span>}
                {wire.autoApply && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>Applies automatically</span>}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
                {describeSource(wiring, wire.kind === "Aggregate"
                  ? { kind: "section", sectionKey: wire.source.sectionKey }
                  : { kind: "segment", sectionKey: wire.source.sectionKey, segmentKey: wire.source.segmentKey ?? "" })}
                {" → "}
                {describeTarget(sections, { sectionKey: wire.target.sectionKey, segmentKey: wire.target.segmentKey ?? "" })}
              </div>
              {wire.problems.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11.5, color: "var(--st-overdue)" }}>
                  {wire.problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="kd-btn" disabled={busy} onClick={() => edit(wire)}>
                  Change
                </button>
                <button
                  className="kd-btn"
                  disabled={busy}
                  onClick={() => void run(() => api.pauseFeedMapping(getAccessToken()!, wire.id, !wire.isPaused))}
                >
                  {wire.isPaused ? "Resume" : "Pause"}
                </button>
                <button
                  className="kd-btn"
                  disabled={busy}
                  aria-label={`Remove ${wire.name}`}
                  onClick={() => void run(() => api.deleteFeedMapping(getAccessToken()!, wire.id))}
                >
                  <Icon name="trash" size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

/**
 * The one decision a drawn wire still carries: how their statuses become yours. Lifted in spirit from the
 * admin dial-link tab's status-translation block, which is the single part of that screen worth keeping —
 * the rest of it is eight dropdowns and no dial.
 */
function WireEditor({
  draft,
  setDraft,
  wiring,
  detail,
  sections,
  problems,
  busy,
  editing,
  onSave,
  onCancel,
}: {
  draft: WireDraft;
  setDraft: (next: WireDraft) => void;
  wiring: FeedWiring;
  detail: InstanceDetail;
  sections: InstanceDetail["sections"];
  problems: string[];
  busy: boolean;
  editing: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  const kind = wireKind(draft.source);
  const theirs = sourceStatuses(wiring, draft);
  const yours = targetStatuses(sections, detail.statusSet.statuses, detail.palettes, detail.dimensions, draft);
  const targetSegment = sections
    .find((s) => s.key === draft.target.sectionKey)
    ?.segments.find((g) => g.key === draft.target.segmentKey);
  const theirSegment = draft.source.kind === "segment"
    ? wiring.sections.find((s) => s.key === draft.source.sectionKey)?.segments.find((g) => g.key === (draft.source as { segmentKey: string }).segmentKey)
    : undefined;

  const set = (over: Partial<WireDraft>) => setDraft({ ...draft, ...over });

  return (
    <div style={{ ...cardStyle, marginTop: 12, borderColor: "var(--accent)" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text)" }}>
        {describeSource(wiring, draft.source)} → {describeTarget(sections, draft.target)}
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
        <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>What to call this</span>
        <input value={draft.name} onChange={(e) => set({ name: e.target.value })} style={{ ...selectStyle, width: "100%" }} />
      </label>

      {/* Itemised ends address an item and an axis; a simple one has neither, so neither is offered. */}
      {wiring.sourceKind === "Itemised" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {kind === "Mirror" && (
            <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              Read their item
              <select value={draft.sourceItemKey ?? ""} onChange={(e) => set({ sourceItemKey: e.target.value || null })} style={selectStyle}>
                <option value="">Choose…</option>
                {(theirSegment?.items ?? []).map((i) => (
                  <option key={i.key} value={i.key}>
                    {i.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            on axis
            <select
              value={draft.sourceDimensionKey ?? ""}
              onChange={(e) => set({ sourceDimensionKey: e.target.value || null, translation: {} })}
              style={selectStyle}
            >
              <option value="">Choose…</option>
              {wiring.dimensions.map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {detail.kind === "Itemised" && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            Set my item
            <select value={draft.targetItemKey ?? ""} onChange={(e) => set({ targetItemKey: e.target.value || null })} style={selectStyle}>
              <option value="">Choose…</option>
              {(targetSegment?.items ?? []).map((i) => (
                <option key={i.key} value={i.key}>
                  {i.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
            on axis
            <select
              value={draft.targetDimensionKey ?? ""}
              onChange={(e) => set({ targetDimensionKey: e.target.value || null, translation: {} })}
              style={selectStyle}
            >
              <option value="">Choose…</option>
              {(detail.dimensions ?? []).map((d) => (
                <option key={d.key} value={d.key}>
                  {d.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {kind === "Mirror" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>When they set…</div>
          {theirs.length === 0 && <p style={hintStyle}>Choose an axis above to see their statuses.</p>}
          {theirs.map((s) => (
            <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
              <Swatch colour={s.colour} />
              <span style={{ minWidth: 110 }}>{s.name}</span>
              <span aria-hidden style={{ color: "var(--text-faint)" }}>→</span>
              <select
                aria-label={`Set my status when theirs is ${s.name}`}
                value={draft.translation[s.key] ?? ""}
                onChange={(e) => set({ translation: { ...draft.translation, [s.key]: e.target.value } })}
                style={selectStyle}
              >
                <option value="">Leave mine alone</option>
                {yours.map((t: GeneratedStatus) => (
                  <option key={t.key} value={t.key}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <p style={hintStyle}>
            Their statuses and yours are different sets — two dials both saying “complete” don’t mean the
            same thing, so nothing is matched up for you.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 12.5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            When
            <select value={draft.when} onChange={(e) => set({ when: e.target.value as WireDraft["when"] })} style={selectStyle}>
              <option value="allTerminal">everything they share here is finished</option>
              <option value="anyOf">any of them is…</option>
            </select>
          </div>

          {draft.when === "anyOf" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, paddingLeft: 4 }}>
              {theirs.map((s) => (
                <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={draft.whenStatusKeys.includes(s.key)}
                    onChange={(e) =>
                      set({
                        whenStatusKeys: e.target.checked
                          ? [...draft.whenStatusKeys, s.key]
                          : draft.whenStatusKeys.filter((k) => k !== s.key),
                      })
                    }
                  />
                  <Swatch colour={s.colour} />
                  {s.name}
                </label>
              ))}
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            set mine to
            <select value={draft.thenStatusKey} onChange={(e) => set({ thenStatusKey: e.target.value })} style={selectStyle}>
              <option value="">Choose…</option>
              {yours.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            otherwise
            <select
              value={draft.otherwiseStatusKey ?? ""}
              onChange={(e) => set({ otherwiseStatusKey: e.target.value || null })}
              style={selectStyle}
            >
              <option value="">leave mine alone</option>
              {yours.map((t) => (
                <option key={t.key} value={t.key}>
                  set it to {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12 }}>
        <input type="checkbox" checked={draft.autoApply} onChange={(e) => set({ autoApply: e.target.checked })} />
        <span>
          Apply their changes without asking me
          <span style={{ display: "block", color: "var(--text-faint)", fontSize: 11.5 }}>
            Off by default. You wrote this against their dial as it is today, and they can change what it
            says at any time without telling you.
          </span>
        </span>
      </label>

      {problems.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "var(--st-overdue)" }}>
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button className="kd-btn-primary" disabled={busy} onClick={onSave}>
          {editing ? "Save this wire" : "Add this wire"}
        </button>
        <button className="kd-btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
